import React, { useCallback } from 'react'
import { P2Accordion, P2Alert, P2DateSelector, P2TextDescription, P2TextInput, P2TextLabel } from 'react-bootstrap-common-script'
import { StageBPersonalDetail, StageBReviewEndorse } from '../../../models/classes/stage-b/review-endorse.class'
import { Dropdown } from '../../../components/dropdown';
import { incomingOwnerCitizenshipDropdownItems, incomingOwnerRelatedToAboveItems, maritalStatusDropdownItems, nationalityDropdownItems, raceDropdownItems, sexDropdownItems } from '../../../utils/shared-dropdown';
import RadioGroup from '../../../components/form/grouped/radio/radio-group';
import { isRelatedToOwner } from '../../../utils/shared-radio';
import { Owner } from '../../../models/polymer/Owner.model';
import { MyInfo } from '../../../models/polymer/MyInfo.model';
import { Helpers } from '../../../utils/helper';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import SingpassMyInfo from '../../../components/singpass/singpass-myinfo';
import { retrieveMyInfoByNRIC } from '../../../redux/slices/authentication-slice';
import { LottieLoader } from '../../../components/loader';
import useRetrieveMyInfoStageB from '../../../hooks/use-retrieve-myinfo-stage-b';
import { ResidentialStatusEnum } from '../../../models/enum/residential-status.enum';
import { checkDOB } from '../../../utils/form-helper';
import { InfoCard } from '../../../components/info';
import DangerCircle from '../../../assets/svg/danger-circle';
import SpouseForm from '../../../components/HOC/spouse-form';
import { MaritalStatusEnum } from '../../../models/enum/marital-status.enum';
import { namePattern } from '../../../utils/shared-pattern';

interface ReviewPersonalDetailsProps {
  values: StageBReviewEndorse;
  firstOwnerName: string;
  personal_values: StageBPersonalDetail;
  setFormValue: React.Dispatch<React.SetStateAction<StageBPersonalDetail>>;
  errors: any;
  isDobMatch: boolean;
  isMaritalStatusMatch: boolean;
  isSpouseIdMatch: boolean;
  handleSpouseIdFieldBlur: () => void;
  isChanged: boolean;
  retrieveFromMyInfo: boolean;
  isLoginRetainingOrOutgoing: boolean;
  isLoginPureRetaining: boolean;
  forceError?: boolean;
  onBindMyInfo?:(myInfoData:MyInfo)=>void
  onUnBindMyInfo?:()=>void
}

function ReviewPersonalDetails({ isChanged, ...props }: ReviewPersonalDetailsProps) {
  const AppDispatch = useAppDispatch();

  const loginUser = useAppSelector(state => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);

  const bindProcess = useCallback((res:any)=>{
    const errorMessage = "We are unable to retrieve your information from Myinfo. Please enter your personal details manually.";
    if(isNaN(res.payload)){
      const myInfoData:MyInfo = new MyInfo(res.payload);
      if(myInfoData.dob && myInfoData.dob.split("-").length < 3) {
        setRetrieve(false)
        alert(errorMessage)
        return;
      }
      if(props.onBindMyInfo){
        props.onBindMyInfo(myInfoData);
      }
    } else {
      setRetrieve(false)
      alert(errorMessage)
    }
  },[props])

  const handleIsRelatedChange = useCallback((value: boolean) => {
    props.setFormValue({ ...props.personal_values, login_user: new Owner({ ...props.personal_values.login_user, is_related: value }) })
  }, [props])

  const handleRelationshipChange = useCallback((value: string) => {
    props.setFormValue({ ...props.personal_values, login_user: new Owner({ ...props.personal_values.login_user, relationship_with_owner: value }) })
  },[props])

  const { isRetrieve, isMyInfoLoading, toShowMyInfo, toggleRetrieveMyInfo, myInfoLoadingValue, setRetrieve } = useRetrieveMyInfoStageB(()=>{
    AppDispatch(retrieveMyInfoByNRIC({ uinfin: loginUser.uinfin, singpass_code: singpassToken, allow_my_info: true })).then(bindProcess).finally(()=>{
      myInfoLoadingValue(false);
    });
  }, ()=>{
    AppDispatch(retrieveMyInfoByNRIC({ uinfin: loginUser.uinfin, singpass_code: singpassToken, allow_my_info: false })).finally(()=>{
      if(props.onUnBindMyInfo && loginUser.uinfin){
        props.onUnBindMyInfo();
      }
      myInfoLoadingValue(false);
    });
  }, loginUser.uinfin);


  const setMyInfoValues = useCallback((key:string) => (e:string)=>{
    let spouse = props.personal_values.login_user.spouse;
    let spouse_id_type = props.personal_values.login_user.spouse_id_type;
    if(key === "marital_status" && e !== MaritalStatusEnum.married){
      spouse = "";
      spouse_id_type = undefined;
    }
    props.setFormValue({ ...props.personal_values, login_user: new Owner({ ...props.personal_values.login_user, spouse, spouse_id_type, myinfo: new MyInfo({ ...props.personal_values.login_user.myinfo, [key.toString()]: key === "uinfin" ? e.toUpperCase() : e }) }) })
  },[props])
  
  const setSpouseDetails = useCallback((key:string) => (e:string)=>{
    props.setFormValue({ ...props.personal_values, login_user: new Owner({ ...props.personal_values.login_user, [key.toString()]: e }) })
  },[props])
  
  const setGrossDomesticIncomValues = useCallback((format_type:boolean) => (e:any)=>{
    let value = Helpers.formatCurrencyInput(e, format_type);
    props.setFormValue({ ...props.personal_values, login_user: { ...props.personal_values.login_user, gross_monthly_income: value } })
  },[props])

  const setSprObtainedDateValues = useCallback(() => (e:any)=>{
    props.setFormValue({ ...props.personal_values, login_user: { ...props.personal_values.login_user, spr_obtained_date: e } })
  },[props])

  const handleValueChange = useCallback((value:any, field:any) => {
    setSpouseDetails(field)(value);
  }, [setSpouseDetails]);

  return (
    <div className='px-4 pt-4 pb-4'>
      <P2TextDescription color="#000" title="Personal Details" />
        <div className='mt-2'>
          <P2Accordion
            defaultOpen={true}
            title={
              <P2TextDescription size='18px' color="#333333" title={!!props.personal_values.login_user.myinfo.name ? props.personal_values.login_user.myinfo.name : loginUser.name} />
            }
          >
            {isMyInfoLoading && <LottieLoader />}

            {!isMyInfoLoading && <>
              {toShowMyInfo && <SingpassMyInfo hasRetrieve={isRetrieve} onClick={toggleRetrieveMyInfo} />}
              {props.personal_values.login_user.structure_order !== "L1" && !props.isLoginRetainingOrOutgoing && <>
                <div style={{ marginTop: 12 }}><P2TextLabel bold="700" color={"#000"} label={"Relationship"} /></div>
                <RadioGroup disabled={false} style={{ marginTop: 24 }} layout='default' items={isRelatedToOwner} onItemSelect={handleIsRelatedChange} value={props.personal_values.login_user?.is_related} required label={`Is this proposed owner related to ${props.firstOwnerName}?`} />

                {props.personal_values.login_user?.is_related && <div style={{ marginTop: 24 }}>
                  <Dropdown
                    disabled={false}
                    required
                    selectedDropdown={props.personal_values.login_user.relationship_with_owner}
                    placeholder={`Please indicate relationship with owner`}
                    label={`How is this proposed occupier related to ${props.firstOwnerName}?`}
                    items={incomingOwnerRelatedToAboveItems}
                    setSelectedDropdown={handleRelationshipChange}
                  />
                </div>}</>}


              <div style={{ marginTop: props.personal_values.login_user.structure_order !== "L1" ? 32 : 12 }}>
                <P2TextLabel bold={700} label={"Personal Details"} />
              </div>

              <div style={{ marginTop: 12 }}>
                <P2TextInput forceError={props.forceError} isError={!props.errors?.login_user?.uinfin?.isValid} errorMsg={props.errors?.login_user?.uinfin?.message} disabled={props.retrieveFromMyInfo} required value={props.personal_values.login_user?.myinfo?.uinfin?.toString()} name='uinfin' label='NRIC' maxLength={9} placeholder='Please enter NRIC' type='text' onChange={setMyInfoValues("uinfin")} />
              </div>
              <div style={{ marginTop: 24 }}>
                <P2TextInput forceError={props.forceError} pattern={namePattern} isError={!props.errors?.login_user?.name?.isValid} errorMsg={props.errors?.login_user?.name?.message}  disabled={props.retrieveFromMyInfo || props.isLoginPureRetaining} required value={props.personal_values.login_user?.myinfo.name?.toString()} name='name' label='Full name' maxLength={256} placeholder='Please enter full name' type='text' onChange={setMyInfoValues("name")} />
              </div>

              <div style={{ marginTop: 24 }}>
                <Dropdown
                  required
                  disabled={props.retrieveFromMyInfo}
                  selectedDropdown={props.personal_values.login_user?.myinfo?.sex}
                  placeholder="Please indicate sex"
                  label="Sex"
                  variant="small"
                  items={sexDropdownItems}
                  setSelectedDropdown={setMyInfoValues("sex")}
                  forceError={props.forceError}
                />
              </div>
              <div style={{ marginTop: 24 }}>
                <Dropdown
                  required
                  disabled={props.retrieveFromMyInfo && !!props.personal_values.login_user?.myinfo?.race}
                  selectedDropdown={props.personal_values.login_user?.myinfo?.race}
                  placeholder="Please indicate race"
                  label="Race"
                  variant="small"
                  items={raceDropdownItems}
                  setSelectedDropdown={setMyInfoValues("race")}
                  forceError={props.forceError}
                />
              </div>
              <div style={{ marginTop: 24 }}>
                <Dropdown
                  required
                  disabled={props.retrieveFromMyInfo}
                  selectedDropdown={props.personal_values.login_user?.myinfo?.nationality}
                  placeholder="Please indicate nationality"
                  label="Nationality"
                  variant="small"
                  items={nationalityDropdownItems}
                  setSelectedDropdown={setMyInfoValues("nationality")}
                  forceError={props.forceError}
                />
              </div>

              <div style={{ marginTop: 24 }}>
                <Dropdown
                  disabled={props.retrieveFromMyInfo}
                  required
                  selectedDropdown={props.personal_values.login_user?.myinfo?.residential_status}
                  placeholder="Please indicate residential status"
                  label="Residential status"
                  items={incomingOwnerCitizenshipDropdownItems}
                  setSelectedDropdown={setMyInfoValues("residential_status")}
                  forceError={props.forceError}
                />
              </div>
              
              {props.personal_values.login_user?.myinfo?.residential_status === ResidentialStatusEnum.pr &&
                <>
                  <div style={{ marginTop: 24 }}>
                    <P2DateSelector
                      value={props.personal_values.login_user?.spr_obtained_date}
                      required
                      placeholder="DD/MM/YYYY"
                      label="Date of obtaining SPR status"
                      onChange={setSprObtainedDateValues()}
                      forceError={props.forceError}
                      errorMsg={props.errors?.login_user?.spr_obtained_date?.message}
                    />
                  </div>
                  {props.personal_values.login_user?.spr_obtained_date && !checkDOB(props.personal_values.login_user?.spr_obtained_date, 3) && 
                  <div style={{ marginTop: 32 }}>
                    <InfoCard
                        className="polymer-alert-card"
                        style={{ backgroundColor: "#FFF0C9", color: "#80460D" }}
                        description={
                          <div>
                            An individual Singapore Permanent Resident (SPR) would need to obtain his/her SPR status for at least 3 years to be eligible as one of the proposed owners, if the other owners are also SPRs.
                          </div>
                        }
                        icon={<DangerCircle color="#80460D" />}
                      />
                  </div>}
                </>
                }



              <div style={{ marginTop: 24 }}>
                <P2DateSelector
                  isError={!props.errors?.login_user?.dob?.isValid}
                  errorMsg={props.errors?.login_user?.dob?.message}
                  disabled={props.retrieveFromMyInfo}
                  value={props.personal_values.login_user?.myinfo?.dob}
                  required
                  placeholder="DD/MM/YYYY"
                  label="Date of birth"
                  onChange={setMyInfoValues("dob")}
                  forceError={props.forceError}
                />
              </div>

              <div style={{ marginTop: 24 }}>
                <Dropdown
                  disabled={false}
                  required
                  selectedDropdown={props.personal_values.login_user?.myinfo?.marital_status}
                  placeholder="Please indicate marital status"
                  label="Marital status"
                  items={maritalStatusDropdownItems}
                  setSelectedDropdown={setMyInfoValues("marital_status")}
                  forceError={props.forceError}
                />
              </div>
              
              <SpouseForm forceError={props.forceError} item={props.personal_values.login_user} disableFields={false} handleSpouseIdFieldBlur={props.handleSpouseIdFieldBlur} handleValueChange={handleValueChange} itemHoldingErrors={props.errors?.login_user}/>
              
              <div style={{ marginTop: 24 }}>
                <P2TextInput forceError={props.forceError} disabled={false} isError={!props.errors?.login_user?.gross_monthly_income?.isValid} errorMsg={props.errors?.login_user?.gross_monthly_income?.message} value={Helpers.formatThousandSeparator(props.personal_values.login_user.gross_monthly_income, "$ ")} maxLength={30} required name='gross_monthly_income' label='Gross monthly income' type='text' onBlur={setGrossDomesticIncomValues(true)} onChange={setGrossDomesticIncomValues(false)} />
              </div>

            </>}

          </P2Accordion>
        </div>
      {isChanged && <div style={{ marginTop: 16 }}>
        <P2Alert variant={"warning"} text={"Any changes to the information provided may affect your eligibility for this application."} />
      </div>}


      {!props.isMaritalStatusMatch && <div style={{ marginTop: 16 }}>
        <P2Alert variant={"warning"} text={"Please note that the Marital status provided by the Main Applicant does not match the information given in the form. Your application will be subjected to another eligibility check."} />
      </div>}

      {!props.isSpouseIdMatch && <div style={{ marginTop: 16 }}>
        <P2Alert variant={"warning"} text={"Please note that the identity number of spouse provided by the Main Applicant does not match the information given in the form. Your application will be subjected to another eligibility check."} />
      </div>}

    </div>
  )
}

export default ReviewPersonalDetails